import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import { battlesApi } from '../../api/battles';
import '../../css/chats/BattleInviteAttachment.css';

/**
 * Вложение-приглашение в баттл.
 *
 * Приглашение создаётся через POST /api/battle_person/invite и приходит
 * получателю как сообщение с вложением InvitationAttachment
 * (invitationType = BATTLE_INVITE). inviteId во вложении — это ID сущности
 * BattlePerson, который используется в эндпоинтах Battle Invite API
 * (/api/battles/invites/{inviteId}/accept | decline | DELETE).
 *
 * Статус приглашения хранится в attachment.status и обновляется бэкендом
 * одновременно с BattlePerson.applicationStatus при accept/decline/revoke.
 *
 * @param {Object} attachment - Данные вложения InvitationAttachment
 * @param {string} attachment.entityId - ID баттла
 * @param {string} attachment.title - Название баттла
 * @param {string} attachment.inviteType - Тип сущности (BATTLE_PERSON)
 * @param {string} attachment.inviteId - ID BattlePerson (для accept/decline/revoke)
 * @param {string} attachment.status - Статус: PENDING, ACCEPTED, DECLINED, REVOKED
 * @param {boolean} isOwn - Является ли текущий пользователь отправителем (организатором)
 * @param {Function} onAction - Callback при действии (action, inviteId)
 */
const STATUS_LABELS = {
    ACCEPTED: 'Принято',
    DECLINED: 'Отклонено',
    REVOKED: 'Отозвано'
};

export default function BattleInviteAttachment({ attachment, isOwn, onAction }) {
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const { entityId: battleId, title, inviteId, status } = attachment;

    // Если приглашение уже обработано (статус из вложения, обновляется бэкендом)
    if (status && status !== 'PENDING' && !success) {
        return (
            <div className="battle-invite-attachment battle-invite-attachment--success">
                <div className="battle-invite-attachment__header">
                    <span className="battle-invite-attachment__icon">🏆</span>
                    <h4 className="battle-invite-attachment__title">Приглашение в баттл</h4>
                </div>
                <div className="battle-invite-attachment__body">
                    <div className="battle-invite-attachment__battle">
                        <Link to={`/battles/${battleId}`} className="battle-invite-attachment__battle-link">
                            {title || 'Баттл'}
                        </Link>
                    </div>
                    <div className="battle-invite-attachment__status">
                        <span className="battle-invite-attachment__status-text">
                            {STATUS_LABELS[status] || 'Обработано'}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    const handleAccept = async () => {
        if (!inviteId) {
            setError('Не удалось определить приглашение. Попробуйте принять на странице баттла.');
            return;
        }

        setActionLoading(true);
        setError(null);

        try {
            await battlesApi.acceptInvite(inviteId);
            setSuccess(true);
            if (onAction) onAction('accept', inviteId);
        } catch (err) {
            console.error('Ошибка при принятии приглашения в баттл:', err);
            setError(err.response?.data?.message || 'Не удалось принять приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDecline = async () => {
        if (!inviteId) {
            setError('Не удалось определить приглашение. Попробуйте отклонить на странице баттла.');
            return;
        }

        setActionLoading(true);
        setError(null);

        try {
            await battlesApi.declineInvite(inviteId);
            setSuccess(true);
            if (onAction) onAction('decline', inviteId);
        } catch (err) {
            console.error('Ошибка при отклонении приглашения в баттл:', err);
            setError(err.response?.data?.message || 'Не удалось отклонить приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevoke = async () => {
        if (!inviteId) {
            setError('Не удалось определить приглашение.');
            return;
        }

        setActionLoading(true);
        setError(null);

        try {
            await battlesApi.revokeInvite(inviteId);
            setSuccess(true);
            if (onAction) onAction('revoke', inviteId);
        } catch (err) {
            console.error('Ошибка при отзыве приглашения в баттл:', err);
            setError(err.response?.data?.message || 'Не удалось отозвать приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    if (success) {
        return (
            <div className="battle-invite-attachment battle-invite-attachment--success">
                <HrnbAlert variant="success" onClose={() => {}}>
                    Приглашение обработано!
                </HrnbAlert>
            </div>
        );
    }

    return (
        <div className="battle-invite-attachment">
            <div className="battle-invite-attachment__header">
                <span className="battle-invite-attachment__icon">🏆</span>
                <h4 className="battle-invite-attachment__title">Приглашение в баттл</h4>
            </div>

            <div className="battle-invite-attachment__body">
                <div className="battle-invite-attachment__battle">
                    <Link to={`/battles/${battleId}`} className="battle-invite-attachment__battle-link">
                        {title || 'Баттл'}
                    </Link>
                </div>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {!isOwn && !success && (
                <div className="battle-invite-attachment__actions">
                    <HrnbButton
                        variant="success"
                        size="sm"
                        onClick={handleAccept}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Принятие...' : 'Принять'}
                    </HrnbButton>
                    <HrnbButton
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDecline}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Отклонение...' : 'Отклонить'}
                    </HrnbButton>
                </div>
            )}

            {isOwn && !success && (
                <div className="battle-invite-attachment__actions">
                    <div className="battle-invite-attachment__status">
                        <span className="battle-invite-attachment__status-text">
                            Ожидает ответа
                        </span>
                    </div>
                    <HrnbButton
                        variant="outline"
                        size="sm"
                        onClick={handleRevoke}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Отзыв...' : 'Отозвать'}
                    </HrnbButton>
                </div>
            )}
        </div>
    );
}
